import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "./AuthContext";
import { storageGet, storageSet } from "../utils/browserSafety";

const TutorialCtx = createContext(null);

export const TUTORIAL_STEPS = [
  {
    key: "welcome",
    title: "Welcome to Collavue.",
    body:
      "Quick tour — about 30 seconds. We'll show you the parts you'll use every day. You can skip anytime.",
    target: null,
    placement: "center",
  },
  {
    key: "dashboard",
    title: "Your dashboard.",
    body:
      "Active campaigns, hot leads, daily activity and credits — all at a glance.",
    target: "nav-dashboard",
    placement: "right",
  },
  {
    key: "campaigns",
    title: "Campaigns.",
    body:
      "Create a campaign to start finding buying-intent posts. Each sync pulls fresh leads.",
    target: "nav-campaigns",
    placement: "right",
  },
  {
    key: "leads",
    title: "Lead Inbox.",
    body:
      "Scored and sorted leads land here — hot ones first. Open one to draft a reply.",
    target: "nav-leads",
    placement: "right",
  },
  {
    key: "brand-voice",
    title: "Brand Voice.",
    body:
      "Train Collavue on how you write so AI-generated replies actually sound like you.",
    target: "nav-brand-voice",
    placement: "right",
  },
  {
    key: "credits",
    title: "Credits.",
    body:
      "Each sync and reply uses credits. Top up here whenever you need more.",
    target: "credits-widget",
    placement: "right",
  },
  {
    key: "new-campaign",
    title: "Create your first campaign.",
    body:
      "Ready when you are. Click here to start — we'll guide you through it.",
    target: "new-campaign-cta",
    placement: "bottom",
  },
];

export function TutorialProvider({ children }) {
  const { user, userProfile, updateUserProfile } = useAuth();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Auto-start for new users who haven't completed or dismissed the tutorial.
  useEffect(() => {
    if (!user || !userProfile) return;
    if (active) return;
    const seen =
      userProfile.tutorialCompleted === true ||
      userProfile.tutorialDismissed === true;
    if (seen) return;
    // Local fallback so a flaky network never re-opens it.
    if (storageGet(`collavue.tutorial.seen.${user.uid}`) === "1") {
      return;
    }
    setStepIndex(0);
    setActive(true);
  }, [user, userProfile, active]);

  const persist = useCallback(
    async (patch) => {
      if (!user) return;
      storageSet(`collavue.tutorial.seen.${user.uid}`, "1");
      try {
        if (updateUserProfile) {
          await updateUserProfile(patch);
        } else {
          await setDoc(doc(db, "users", user.uid), patch, { merge: true });
        }
      } catch (err) {
        console.warn("tutorial persist failed:", err);
      }
    },
    [user, updateUserProfile]
  );

  const start = useCallback(() => {
    setStepIndex(0);
    setActive(true);
  }, []);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (i + 1 >= TUTORIAL_STEPS.length) {
        setActive(false);
        persist({ tutorialCompleted: true });
        return i;
      }
      return i + 1;
    });
  }, [persist]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const skip = useCallback(() => {
    setActive(false);
    persist({ tutorialDismissed: true });
  }, [persist]);

  const complete = useCallback(() => {
    setActive(false);
    persist({ tutorialCompleted: true });
  }, [persist]);

  const value = useMemo(
    () => ({
      active,
      stepIndex,
      step: TUTORIAL_STEPS[stepIndex],
      totalSteps: TUTORIAL_STEPS.length,
      start,
      next,
      back,
      skip,
      complete,
    }),
    [active, stepIndex, start, next, back, skip, complete]
  );

  return <TutorialCtx.Provider value={value}>{children}</TutorialCtx.Provider>;
}

export function useTutorial() {
  const ctx = useContext(TutorialCtx);
  if (!ctx) throw new Error("useTutorial must be used within TutorialProvider");
  return ctx;
}
